/**
 * 我的资料主线程透传层（14.29a）。
 *
 * profile 数据本身**刻意不进 GlobalState**：它是「我的 Tab/编辑资料页」的页面级
 * 数据，瞬态组件态「null 则 load」口径（loadGroupMembers 先例）——Worker 会话期
 * 缓存命中零 HTTP，改资料成功后 Worker 已 patch 缓存，页面重调 loadMyProfile
 * 即读新值（零回读）。无推送通道：改资料只有本端主动发起（§7.4 无 WS 推送）。
 *
 * ⚠️ **但「该重读了」这个信号必须进 GlobalState**（21o）：原设计默认「页面重挂就会
 * 重读」，而 21o 把桌面改成三列后，**左栏的「我的」Tab 一直挂着不再重挂**——
 * 在右栏改完昵称返回，左栏卡片还是旧名字。所以这里在每次**改资料成功**后把
 * `GlobalState.profileRev` 加一，页面把它放进 load 的依赖里即可被动刷新。
 * 只存一个计数器而不是整份 profile：避免把页面级数据提到全局，也不触发无关重渲染。
 *
 * 错误口径：load 失败折 null（页面重试钮）；写操作业务错 reject 带服务端
 * 文案由页面 toast 直出（contactsState 写接口同口径）。
 */

import type { AppWorkerHost } from './authState';
import type { GlobalStore } from './global';
import type {
  DeviceListPage,
  FeedbackListPage,
  MyProfile,
  ProfileChangeField,
  SubmitFeedbackInput,
  TwoFaSetup,
} from '~/worker/contract';

export interface UserProfileConnection {
  /** null = 未登录/拉取失败（页面重试钮再调）。缓存命中零 HTTP */
  /** `force=true` 丢 Worker 缓存重拉（下拉刷新）；默认吃缓存零 HTTP */
  loadMyProfile(force?: boolean): Promise<MyProfile | null>;
  /** 文本/隐私字段单改。false=守门拒；业务错 reject 文案直出 */
  changeProfileField(field: ProfileChangeField, value: string): Promise<boolean>;
  /** 换头像（压缩管线→uploadv2→change，Worker 内编排）。false=大小不合规 */
  changeMyAvatar(avatar: { blob: Blob; filename: string }): Promise<boolean>;
  /** 改密码（带旧密码；首登设密码走 authActions.setPassword 不经此） */
  changeMyPassword(oldPassword: string, newPassword: string): Promise<boolean>;
  bindPhone(params: { phone: string; code: string; countryCode: number }): Promise<boolean>;
  bindEmail(params: { email: string; code: string }): Promise<boolean>;
  changeEmail(params: { email: string; code: string }): Promise<boolean>;
  /** 换手机四步（§7.5；仅 +86）。verifyOldCode 成功返回 ChangeToken（5 分钟） */
  changeMobileSendOldCode(): Promise<boolean>;
  changeMobileVerifyOldCode(code: string): Promise<string | null>;
  changeMobileSendNewCode(params: {
    changeToken: string;
    newPhone: string;
    countryCode: number;
  }): Promise<boolean>;
  changeMobileConfirm(params: {
    changeToken: string;
    newPhone: string;
    countryCode: number;
    code: string;
  }): Promise<boolean>;
  /** 登录设备分页（14.34b-3；§7.3 只读刻意不缓存——1 请求/页）。null=未登录/失败 */
  loadDevices(page: number): Promise<DeviceListPage | null>;
  /** 2FA 绑定材料（不落库，bind 校验通过才生效）。null=未登录/失败（页面重试） */
  generateTwoFa(): Promise<TwoFaSetup | null>;
  /** false=守门拒（空码）零 HTTP；业务错（验证码错误）reject 文案直出 */
  bindTwoFa(secret: string, code: string): Promise<boolean>;
  unbindTwoFa(code: string): Promise<boolean>;
  /** 我的反馈分页（14.35a；devices 同口径不缓存——1 请求/页）。null=未登录/失败 */
  loadFeedbackList(page: number): Promise<FeedbackListPage | null>;
  /** 提交反馈（截图上传编排在 Worker）。false=守门拒；业务错 reject 文案直出 */
  submitFeedback(input: SubmitFeedbackInput): Promise<boolean>;
  /** 删除反馈记录。false=守门拒；业务错 reject 文案直出 */
  removeFeedback(ids: number[]): Promise<boolean>;
}

export function connectUserProfile(
  store: GlobalStore,
  host: AppWorkerHost,
): UserProfileConnection {
  /** 改资料成功 → 版本号 +1（见文件头）。失败不动，避免无谓重读 */
  const bump = <T>(p: Promise<T>, changed: (v: T) => boolean): Promise<T> =>
    p.then((v) => {
      if (changed(v)) {
        store.set((prev) => ({ ...prev, profileRev: prev.profileRev + 1 }));
      }
      return v;
    });
  const truthy = (v: boolean): boolean => v;
  return {
    loadMyProfile: (force = false) =>
      host.call('user/profile/load', force ? { force: true } : null).catch(() => null),
    changeProfileField: (field, value) =>
      bump(host.call('user/profile/change', { field, value }), truthy),
    changeMyAvatar: (avatar) => bump(host.call('user/avatar/change', { avatar }), truthy),
    changeMyPassword: (oldPassword, newPassword) =>
      host.call('user/password/change', { oldPassword, newPassword }),
    bindPhone: (params) => host.call('user/phone/bind', params),
    bindEmail: (params) => host.call('user/email/bind', params),
    changeEmail: (params) => host.call('user/email/change', params),
    changeMobileSendOldCode: () => host.call('user/mobile/sendOldCode', null),
    changeMobileVerifyOldCode: (code) => host.call('user/mobile/verifyOldCode', { code }),
    changeMobileSendNewCode: (params) => host.call('user/mobile/sendNewCode', params),
    changeMobileConfirm: (params) => host.call('user/mobile/confirm', params),
    loadDevices: (page) => host.call('user/devices/load', { page }).catch(() => null),
    generateTwoFa: () => host.call('user/2fa/generate', null).catch(() => null),
    bindTwoFa: (secret, code) => host.call('user/2fa/bind', { secret, code }),
    unbindTwoFa: (code) => host.call('user/2fa/unbind', { code }),
    loadFeedbackList: (page) => host.call('user/feedback/list', { page }).catch(() => null),
    submitFeedback: (input) => host.call('user/feedback/add', input),
    removeFeedback: (ids) => host.call('user/feedback/remove', { ids }),
  };
}
